import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { admin } from '../../lib/api';
import type {
  Game,
  GameType,
  HomeOrAway,
  LivePrivacy,
  Opponent,
  Season,
  Tournament,
  Lineup,
  TeamUniform,
} from '../../types';
import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';
import { Checkbox, CheckboxField } from '../../ui/checkbox';
import { ErrorMessage, Field, Label } from '../../ui/fieldset';
import { Heading } from '../../ui/heading';
import { Input } from '../../ui/input';
import { Select } from '../../ui/select';
import { Textarea } from '../../ui/textarea';
import { Text } from '../../ui/text';
import { EmptyState, Panel, StatCard } from '../../components/PageChrome';
import { LocationPinPicker } from '../../components/LocationMap';

const GAME_TYPES: { value: GameType; label: string }[] = [
  { value: 'REGULAR_SEASON', label: 'Regular season' },
  { value: 'TOURNAMENT', label: 'Tournament' },
  { value: 'SCRIMMAGE', label: 'Scrimmage' },
  { value: 'PLAYOFF', label: 'Playoff' },
  { value: 'PRACTICE', label: 'Practice' },
  { value: 'OTHER', label: 'Other' },
];

const fmtDateTime = (iso: string) =>
  new Date(iso).toLocaleString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });

type StatusBadgeColor =
  | 'zinc'
  | 'red'
  | 'amber'
  | 'lime'
  | 'rose';

const statusBadge = (s: Game['status']): { label: string; color: StatusBadgeColor } => {
  switch (s) {
    case 'SCHEDULED':
      return { label: 'Scheduled', color: 'zinc' };
    case 'LIVE':
      return { label: 'Live', color: 'red' };
    case 'RECORDING':
      return { label: 'Recording', color: 'amber' };
    case 'READY_FOR_REVIEW':
      return { label: 'Awaiting review', color: 'amber' };
    case 'READY':
      return { label: 'Ready', color: 'lime' };
    case 'FAILED':
      return { label: 'Failed', color: 'rose' };
  }
};

interface FormState {
  scheduledAt: string;
  opponent: string;
  opponentId: string;
  venue: string;
  warmupAt: string;
  warmupLocationName: string;
  warmupAddress: string;
  warmupLatitude: number | undefined;
  warmupLongitude: number | undefined;
  homeOrAway: HomeOrAway | '';
  gameType: GameType;
  seasonId: string;
  tournamentId: string;
  uniformId: string;
  livePrivacy: LivePrivacy;
  aiAssisted: boolean;
  notes: string;
}

const emptyForm = (): FormState => ({
  scheduledAt: '',
  opponent: '',
  opponentId: '',
  venue: '',
  warmupAt: '',
  warmupLocationName: '',
  warmupAddress: '',
  warmupLatitude: undefined,
  warmupLongitude: undefined,
  homeOrAway: 'HOME',
  gameType: 'REGULAR_SEASON',
  seasonId: '',
  tournamentId: '',
  uniformId: '',
  livePrivacy: 'PUBLIC',
  aiAssisted: false,
  notes: '',
});

const fromGame = (g: Game): FormState => ({
  scheduledAt: g.scheduledAt ? toLocalInput(g.scheduledAt) : '',
  opponent: g.opponent ?? '',
  opponentId: g.opponentId ?? '',
  venue: g.venue ?? '',
  warmupAt: g.warmupAt ? toLocalInput(g.warmupAt) : '',
  warmupLocationName: g.warmupLocationName ?? '',
  warmupAddress: g.warmupAddress ?? '',
  warmupLatitude: g.warmupLatitude,
  warmupLongitude: g.warmupLongitude,
  homeOrAway: g.homeOrAway ?? '',
  gameType: g.gameType ?? 'REGULAR_SEASON',
  seasonId: g.seasonId ?? '',
  tournamentId: g.tournamentId ?? '',
  uniformId: g.uniformId ?? '',
  livePrivacy: g.livePrivacy ?? 'PUBLIC',
  aiAssisted: g.aiAssisted ?? false,
  notes: g.notes ?? '',
});

function toLocalInput(iso: string): string {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function fromLocalInput(local: string): string {
  return new Date(local).toISOString();
}

interface GameFormProps {
  initial: FormState;
  opponents: Opponent[];
  seasons: Season[];
  tournaments: Tournament[];
  uniforms: TeamUniform[];
  onSubmit: (form: FormState) => Promise<void>;
  onCancel: () => void;
  submitLabel: string;
}

function GameForm({
  initial,
  opponents,
  seasons,
  tournaments,
  uniforms,
  onSubmit,
  onCancel,
  submitLabel,
}: GameFormProps) {
  const [form, setForm] = useState<FormState>(initial);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.scheduledAt) {
      setError('Date & time is required');
      return;
    }
    if (!form.opponent.trim()) {
      setError('Opponent is required');
      return;
    }
    setBusy(true);
    setError(undefined);
    try {
      await onSubmit(form);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const onPickOpponent = (id: string) => {
    if (!id) {
      setForm({ ...form, opponentId: '' });
      return;
    }
    const opp = opponents.find((o) => o.opponentId === id);
    setForm({
      ...form,
      opponentId: id,
      opponent: opp?.name ?? form.opponent,
    });
  };

  return (
    <form onSubmit={submit} className="space-y-4">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Field>
          <Label>Date &amp; time</Label>
          <Input
            required
            type="datetime-local"
            value={form.scheduledAt}
            onChange={(e) => setForm({ ...form, scheduledAt: e.target.value })}
          />
        </Field>
        <Field>
          <Label>Home / Away</Label>
          <Select
            value={form.homeOrAway}
            onChange={(e) =>
              setForm({ ...form, homeOrAway: e.target.value as HomeOrAway | '' })
            }
          >
            <option value="HOME">Home</option>
            <option value="AWAY">Away</option>
            <option value="">—</option>
          </Select>
        </Field>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <Field>
          <Label>Game type</Label>
          <Select
            value={form.gameType}
            onChange={(e) =>
              setForm({
                ...form,
                gameType: e.target.value as GameType,
                tournamentId:
                  e.target.value === 'TOURNAMENT' ? form.tournamentId : '',
              })
            }
          >
            {GAME_TYPES.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </Select>
        </Field>
        <Field>
          <Label>Season</Label>
          <Select
            value={form.seasonId}
            onChange={(e) => setForm({ ...form, seasonId: e.target.value })}
          >
            <option value="">— None —</option>
            {seasons.map((s) => (
              <option key={s.seasonId} value={s.seasonId}>
                {s.name}
              </option>
            ))}
          </Select>
        </Field>
        {form.gameType === 'TOURNAMENT' ? (
          <Field>
            <Label>Tournament</Label>
            <Select
              value={form.tournamentId}
              onChange={(e) => setForm({ ...form, tournamentId: e.target.value })}
            >
              <option value="">— Pick a tournament —</option>
              {tournaments.map((t) => (
                <option key={t.tournamentId} value={t.tournamentId}>
                  {t.name}
                </option>
              ))}
            </Select>
          </Field>
        ) : null}
      </div>

      <Field>
        <Label>Opponent</Label>
        {opponents.length > 0 ? (
          <Select
            value={form.opponentId}
            onChange={(e) => onPickOpponent(e.target.value)}
            className="mb-2"
          >
            <option value="">— Pick from saved opponents —</option>
            {opponents.map((o) => (
              <option key={o.opponentId} value={o.opponentId}>
                {o.name}
              </option>
            ))}
          </Select>
        ) : null}
        <Input
          required
          value={form.opponent}
          onChange={(e) => setForm({ ...form, opponent: e.target.value })}
          placeholder="Opponent name"
        />
      </Field>

      <Field>
        <Label>Venue</Label>
        <Input
          value={form.venue}
          onChange={(e) => setForm({ ...form, venue: e.target.value })}
          placeholder="Field / location (optional)"
        />
      </Field>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <Field>
          <Label>Uniform</Label>
          <Select value={form.uniformId} onChange={(e) => setForm({ ...form, uniformId: e.target.value })}>
            <option value="">— None —</option>
            {uniforms
              .filter((uniform) => uniform.active || uniform.uniformId === form.uniformId)
              .map((uniform) => (
                <option key={uniform.uniformId} value={uniform.uniformId}>
                  {uniform.name}{uniform.active ? '' : ' (inactive)'}
                </option>
              ))}
          </Select>
        </Field>
        <Field>
          <Label>Warmup time</Label>
          <Input
            type="datetime-local"
            value={form.warmupAt}
            onChange={(e) => setForm({ ...form, warmupAt: e.target.value })}
          />
        </Field>
        <Field>
          <Label>Warmup place</Label>
          <Input
            value={form.warmupLocationName}
            onChange={(e) => setForm({ ...form, warmupLocationName: e.target.value })}
          />
        </Field>
      </div>

      <Field>
        <Label>Warmup address</Label>
        <Input
          value={form.warmupAddress}
          onChange={(e) => setForm({ ...form, warmupAddress: e.target.value })}
        />
      </Field>

      <Field className="space-y-2">
        <Label>Warmup pin</Label>
        <LocationPinPicker
          value={{ latitude: form.warmupLatitude, longitude: form.warmupLongitude }}
          onChange={(pin) =>
            setForm({ ...form, warmupLatitude: pin.latitude, warmupLongitude: pin.longitude })
          }
        />
        {form.warmupLatitude !== undefined && form.warmupLongitude !== undefined ? (
          <div className="flex flex-wrap items-center gap-3">
            <Text className="font-mono text-xs">{form.warmupLatitude}, {form.warmupLongitude}</Text>
            <Button
              type="button"
              outline
              onClick={() => setForm({ ...form, warmupLatitude: undefined, warmupLongitude: undefined })}
            >
              Clear pin
            </Button>
          </div>
        ) : null}
      </Field>

      <Field>
        <Label>Notes</Label>
        <Textarea
          rows={2}
          value={form.notes}
          onChange={(e) => setForm({ ...form, notes: e.target.value })}
        />
      </Field>

      <Field>
        <Label>Live stream privacy</Label>
        <Select
          value={form.livePrivacy}
          onChange={(e) =>
            setForm({ ...form, livePrivacy: e.target.value as LivePrivacy })
          }
        >
          <option value="PUBLIC">Public — anyone with the URL can watch</option>
          <option value="PRIVATE">Private — only signed-in viewers can watch</option>
        </Select>
        <Text className="mt-1 text-xs">
          This controls who can watch the live game. Replays follow the same team access rules.
        </Text>
      </Field>

      <CheckboxField>
        <Checkbox
          checked={form.aiAssisted}
          onChange={(checked) => setForm({ ...form, aiAssisted: checked })}
        />
        <Label>
          AI-assisted scoring (live + post-game review). ~$30–40 per game in Bedrock costs.
        </Label>
      </CheckboxField>

      {error ? <ErrorMessage>{error}</ErrorMessage> : null}

      <div className="flex gap-2">
        <Button type="submit" color="dark/zinc" disabled={busy}>
          {busy ? 'Saving…' : submitLabel}
        </Button>
        <Button type="button" outline onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </form>
  );
}

export default function Games() {
  const { teamId } = useParams<{ teamId: string }>();

  const [games, setGames] = useState<Game[]>([]);
  const [opponents, setOpponents] = useState<Opponent[]>([]);
  const [seasonsList, setSeasonsList] = useState<Season[]>([]);
  const [tournamentsList, setTournamentsList] = useState<Tournament[]>([]);
  const [uniforms, setUniforms] = useState<TeamUniform[]>([]);
  const [lineups, setLineups] = useState<Record<string, Lineup | null>>({});
  const [error, setError] = useState<string>();
  const [showNew, setShowNew] = useState(false);
  const [editingId, setEditingId] = useState<string>();

  const load = async () => {
    if (!teamId) return;
    try {
      const [gres, ores, sres, tres, ures] = await Promise.all([
        admin.listGames(teamId),
        admin.listOpponents(teamId),
        admin.listSeasons(teamId),
        admin.listTournaments(teamId),
        admin.listUniforms(teamId),
      ]);
      setGames(gres.games);
      setOpponents(ores.opponents);
      setSeasonsList(sres.seasons);
      setTournamentsList(tres.tournaments);
      setUniforms(ures.uniforms);
      const lineupRows = await Promise.all(
        gres.games.map(async (game) => [game.gameId, await admin.getLineup(game.gameId)] as const),
      );
      setLineups(Object.fromEntries(lineupRows));
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teamId]);

  const upcoming = useMemo(
    () =>
      [...games]
        .filter((g) => g.status === 'SCHEDULED' || g.status === 'LIVE')
        .sort((a, b) => a.scheduledAt.localeCompare(b.scheduledAt)),
    [games],
  );
  const past = useMemo(
    () =>
      [...games]
        .filter((g) => g.status !== 'SCHEDULED' && g.status !== 'LIVE')
        .sort((a, b) => b.scheduledAt.localeCompare(a.scheduledAt)),
    [games],
  );

  const handleCreate = async (form: FormState) => {
    if (!teamId) return;
    await admin.scheduleGame(teamId, {
      scheduledAt: fromLocalInput(form.scheduledAt),
      opponent: form.opponent.trim(),
      opponentId: form.opponentId || undefined,
      venue: form.venue.trim() || undefined,
      warmupAt: form.warmupAt ? fromLocalInput(form.warmupAt) : undefined,
      warmupLocationName: form.warmupLocationName.trim() || undefined,
      warmupAddress: form.warmupAddress.trim() || undefined,
      warmupLatitude: form.warmupLatitude,
      warmupLongitude: form.warmupLongitude,
      homeOrAway: form.homeOrAway || undefined,
      gameType: form.gameType,
      seasonId: form.seasonId || undefined,
      tournamentId:
        form.gameType === 'TOURNAMENT' && form.tournamentId
          ? form.tournamentId
          : undefined,
      uniformId: form.uniformId || undefined,
      livePrivacy: form.livePrivacy,
      aiAssisted: form.aiAssisted,
      notes: form.notes.trim() || undefined,
    });
    setShowNew(false);
    await load();
  };

  const handleUpdate = (gameId: string) => async (form: FormState) => {
    if (!teamId) return;
    await admin.updateGame(teamId, gameId, {
      scheduledAt: fromLocalInput(form.scheduledAt),
      opponent: form.opponent.trim(),
      opponentId: form.opponentId || null,
      venue: form.venue.trim() || null,
      warmupAt: form.warmupAt ? fromLocalInput(form.warmupAt) : null,
      warmupLocationName: form.warmupLocationName.trim() || null,
      warmupAddress: form.warmupAddress.trim() || null,
      warmupLatitude: form.warmupLatitude ?? null,
      warmupLongitude: form.warmupLongitude ?? null,
      homeOrAway: form.homeOrAway || null,
      gameType: form.gameType,
      seasonId: form.seasonId || null,
      tournamentId:
        form.gameType === 'TOURNAMENT' && form.tournamentId ? form.tournamentId : null,
      uniformId: form.uniformId || null,
      livePrivacy: form.livePrivacy,
      aiAssisted: form.aiAssisted,
      notes: form.notes.trim() || null,
    });
    setEditingId(undefined);
    await load();
  };

  const remove = async (g: Game) => {
    if (!teamId) return;
    if (!window.confirm(`Delete game vs ${g.opponent} on ${fmtDateTime(g.scheduledAt)}?`))
      return;
    try {
      await admin.deleteGame(teamId, g.gameId);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  const renderGame = (g: Game) => {
    if (editingId === g.gameId) {
      return (
        <li key={g.gameId}>
          <Panel>
            <GameForm
              initial={fromGame(g)}
              opponents={opponents}
              seasons={seasonsList}
              tournaments={tournamentsList}
              uniforms={uniforms}
              onSubmit={handleUpdate(g.gameId)}
              onCancel={() => setEditingId(undefined)}
              submitLabel="Save game"
            />
          </Panel>
        </li>
      );
    }
    const status = statusBadge(g.status);
    const seasonName = g.seasonId
      ? seasonsList.find((s) => s.seasonId === g.seasonId)?.name
      : undefined;
    const tournamentName = g.tournamentId
      ? tournamentsList.find((t) => t.tournamentId === g.tournamentId)?.name
      : undefined;
    const typeLabel = GAME_TYPES.find((t) => t.value === g.gameType)?.label;
    const lineup = lineups[g.gameId];
    const lineupCount = lineup ? lineup.battingOrder.length + lineup.bench.length : 0;
    return (
      <li key={g.gameId} className="rounded-lg border border-zinc-800 bg-zinc-900 p-4">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <div className="font-medium text-zinc-100">
                {g.homeOrAway === 'AWAY' ? '@' : 'vs'} {g.opponent}
              </div>
              <Badge color={status.color}>{status.label}</Badge>
              <Badge color={g.livePrivacy === 'PRIVATE' ? 'amber' : 'emerald'}>
                {g.livePrivacy === 'PRIVATE' ? 'Private' : 'Public'}
              </Badge>
              {g.aiAssisted ? <Badge color="sky">AI</Badge> : null}
              <Badge color={lineupCount > 0 ? 'lime' : 'amber'}>
                {lineupCount > 0 ? `${lineupCount} on card` : 'Roster/Lineup not set'}
              </Badge>
            </div>
            <div className="mt-1 text-sm text-zinc-400">
              {fmtDateTime(g.scheduledAt)}
              {g.venue ? ` · ${g.venue}` : ''}
              {g.warmupAt ? ` · warmup ${fmtDateTime(g.warmupAt)}` : ''}
              {g.uniform ? ` · ${g.uniform.name}` : ''}
              {typeLabel && g.gameType !== 'REGULAR_SEASON' ? ` · ${typeLabel}` : ''}
              {tournamentName ? ` · ${tournamentName}` : ''}
              {seasonName ? ` · ${seasonName}` : ''}
            </div>
            {g.notes ? <Text className="mt-2 text-sm">{g.notes}</Text> : null}
          </div>

          <div className="flex flex-wrap gap-2 lg:justify-end">
            {g.status === 'SCHEDULED' || g.status === 'LIVE' ? (
              <>
                <Button href={`/admin/teams/${teamId}/games/${g.gameId}/golive`} color={g.status === 'LIVE' ? 'red' : 'dark/zinc'}>
                  {g.status === 'LIVE' ? 'Live now' : 'Go Live'}
                </Button>
                <Button href={`/admin/teams/${teamId}/games/${g.gameId}/score`} outline>
                  Score
                </Button>
              </>
            ) : null}
            {g.status === 'RECORDING' || g.status === 'READY' ? (
              <Button href={`/admin/teams/${teamId}/games/${g.gameId}/score`} outline>
                Score
              </Button>
            ) : null}
            {g.status === 'READY_FOR_REVIEW' || g.status === 'READY' ? (
              <Button href={`/admin/teams/${teamId}/games/${g.gameId}/review`} outline>
                Review
              </Button>
            ) : null}
            <Button href={`/admin/teams/${teamId}/games/${g.gameId}/lineup`} outline>
              {lineupCount > 0 ? 'Lineup' : 'Set roster'}
            </Button>
            <Button
              plain
              onClick={() => {
                setEditingId(g.gameId);
                setShowNew(false);
              }}
            >
              Edit
            </Button>
            {g.status === 'SCHEDULED' ? (
              <Button plain className="text-red-400" onClick={() => void remove(g)}>
                Delete
              </Button>
            ) : null}
          </div>
        </div>
      </li>
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <Heading level={2}>Games</Heading>
          <Text className="mt-1 text-sm">
            Schedule games, prepare the lineup, score live, and share replays when they are ready.
          </Text>
        </div>
        <Button
          color="dark/zinc"
          onClick={() => {
            setShowNew((v) => !v);
            setEditingId(undefined);
          }}
        >
          {showNew ? 'Cancel' : 'Schedule game'}
        </Button>
      </div>

      <div className="grid gap-4 sm:grid-cols-4">
        <StatCard label="Upcoming" value={upcoming.length} />
        <StatCard label="Past" value={past.length} />
        <StatCard label="Live" value={games.filter((g) => g.status === 'LIVE').length} tone="red" />
        <StatCard label="Review" value={games.filter((g) => g.status === 'READY_FOR_REVIEW').length} tone="amber" />
      </div>

      {showNew ? (
        <Panel>
          <GameForm
            initial={emptyForm()}
            opponents={opponents}
            seasons={seasonsList}
            tournaments={tournamentsList}
            uniforms={uniforms}
            onSubmit={handleCreate}
            onCancel={() => setShowNew(false)}
            submitLabel="Schedule game"
          />
        </Panel>
      ) : null}

      {error ? <Text className="text-red-400">{error}</Text> : null}

      <section className="space-y-2">
        <div className="flex items-center justify-between">
          <Heading level={3} className="text-sm text-zinc-400">Upcoming</Heading>
          <Text className="text-xs text-zinc-500">Scheduled and live games</Text>
        </div>
        {upcoming.length === 0 ? (
          <EmptyState
            title="No upcoming games"
            body="Schedule a game to unlock lineup, Go Live, and scorekeeper workflows."
            action={<Button color="dark/zinc" onClick={() => setShowNew(true)}>Schedule game</Button>}
          />
        ) : (
          <ul className="space-y-3">
            {upcoming.map(renderGame)}
          </ul>
        )}
      </section>

      <section className="space-y-2">
        <Heading level={3} className="text-sm text-zinc-400">Past</Heading>
        {past.length === 0 ? (
          <EmptyState
            title="No past games yet"
            body="Finished games and replays will appear here."
          />
        ) : (
          <ul className="space-y-3">
            {past.map(renderGame)}
          </ul>
        )}
      </section>
    </div>
  );
}
